import { isBroadcastWindow } from "../broadcast/windowMode";

/**
 * Generic listener signature for the internal audio event bus
 * (`watch`/`unwatch`/`triggerEvent`). Payloads vary per event
 * (e.g. `{ stream, local }` for "state"), so the listener accepts
 * arbitrary args; consumers narrow the payload at the call site.
 */

type AudioStreamListener = (...args: unknown[]) => void;

/** Clave (compartida entre la ventana de control y el OSR) del monitor local por parlantes. */
const LOCAL_MONITOR_KEY = "@Eriol/audio/local-enabled";

/**
 * GlobalAudio vivo (el último construido y no destruido). El encoder de audio del OSR toma de acá
 * el AudioContext de la mezcla para engancharle el tap de PCM sin abrir otro contexto (cada
 * AudioContext extra es un stream de salida de audio más).
 */
let currentGlobalAudio: GlobalAudio | null = null;

export function getGlobalAudio(): GlobalAudio | null {
  return currentGlobalAudio;
}

function setCurrentGlobalAudio(audio: GlobalAudio | null) {
  currentGlobalAudio = audio;
}

class GlobalAudio {
  AudioStreamContext: AudioContext;

  AudioStreamDestination: MediaStreamAudioDestinationNode;

  /**
   * Contexto del monitor local (parlantes del operador). En el OSR (`?mode=broadcast`) nadie
   * escucha esta ventana: se crea igual (los consumidores la leen) pero queda SUSPENDIDO y sin
   * nada conectado, así no reproduce la mezcla por segunda vez ni gasta un stream de salida.
   */
  AudioLocalContext: AudioContext;

  /** false en el OSR: sin camino de monitor (localGainNode queda null). */
  private readonly hasLocalMonitor = !isBroadcastWindow();

  private localGainNode: GainNode | null = null;

  private visualizerAnalyser: AnalyserNode | null = null;

  private visualizerSource: MediaStreamAudioSourceNode | null = null;

  private audioSources: Map<
    string,
    {
      source: MediaElementAudioSourceNode | MediaStreamAudioSourceNode;

      gainNode: GainNode;

      analyserNode: AnalyserNode;
    }
  > = new Map();

  private eventCallbacks: Map<string, AudioStreamListener[]> = new Map();

  // Cache de clips de audio decodificados (alertas) por src.

  private clipCache: Map<string, AudioBuffer> = new Map();

  constructor() {
    this.AudioStreamContext = new AudioContext();

    this.AudioStreamDestination =
      this.AudioStreamContext.createMediaStreamDestination();

    this.AudioLocalContext = new AudioContext();

    setCurrentGlobalAudio(this);

    this.initialize();

    return this;
  }

  private initialize() {
    if (!this.hasLocalMonitor) {
      // OSR: la mezcla (AudioStreamContext → AudioStreamDestination) es idéntica y es lo que
      // kero recibe; solo se omite el monitor. El contexto local se suspende para soltar el
      // dispositivo de salida.
      this.AudioLocalContext.suspend().catch(() => {
        /* contexto ya cerrado */
      });

      return;
    }

    const localAudioEnabled = isLocalMonitorEnabled();

    // Local audio routing: read mixed stream into AudioLocalContext → speakers

    const localSource = this.AudioLocalContext.createMediaStreamSource(
      this.AudioStreamDestination.stream,
    );

    this.localGainNode = this.AudioLocalContext.createGain();

    this.localGainNode.gain.setValueAtTime(
      localAudioEnabled ? 1 : 0,

      this.AudioLocalContext.currentTime,
    );

    localSource.connect(this.localGainNode);

    this.localGainNode.connect(this.AudioLocalContext.destination);

    // Ensure Electron window is NOT muted (audio control is via gain node only)

    this.ensureWindowUnmuted();
  }

  /** Reproduce un clip one-shot (alerta) en el mix de emisión. Como el monitor local lee el stream
   *  de AudioStreamDestination, conectar ahí lo hace sonar tanto para los viewers (broadcast) como
   *  para el operador (monitor). En la ventana OSR esto entra al stream NVENC; en el control, al
   *  monitor. No bloquea: descarta silenciosamente si el clip falla. */

  async playOneShot(src: string, volumePct = 70): Promise<void> {
    try {
      const buffer = await this.loadClip(src);

      if (!buffer) return;

      const ctx = this.AudioStreamContext;

      if (ctx.state === "suspended") {
        try {
          await ctx.resume();
        } catch {
          /* ignore */
        }
      }

      const node = ctx.createBufferSource();

      node.buffer = buffer;

      const gain = ctx.createGain();

      gain.gain.value = Math.max(0, Math.min(1, volumePct / 100));

      node.connect(gain);

      gain.connect(this.AudioStreamDestination);

      node.start();

      node.onended = () => {
        try {
          node.disconnect();

          gain.disconnect();
        } catch {
          /* ignore */
        }
      };
    } catch (e) {
      console.warn("[GlobalAudio] playOneShot failed:", e);
    }
  }

  private async loadClip(src: string): Promise<AudioBuffer | null> {
    const cached = this.clipCache.get(src);

    if (cached) return cached;

    try {
      const res = await fetch(src);

      const arr = await res.arrayBuffer();

      const buf = await this.AudioStreamContext.decodeAudioData(arr);

      this.clipCache.set(src, buf);

      return buf;
    } catch (e) {
      console.warn("[GlobalAudio] clip load failed:", src, e);

      return null;
    }
  }

  private async ensureWindowUnmuted() {
    if (typeof window !== "undefined" && window.electronAPI) {
      try {
        const state = await window.electronAPI.isAudioMuted();

        if (state.success && state.muted) {
          await window.electronAPI.setAudioMuted(false);
        }
      } catch {
        // ignore
      }
    }
  }

  async setOutputContext(device: MediaDeviceInfo) {
    // Sin monitor (OSR) no hay salida que redirigir.

    if (!this.hasLocalMonitor) return;

    // El AudioContext pudo cerrarse (destroy() en una navegación/teardown)

    // mientras esta llamada estaba pendiente. setSinkId sobre un contexto

    // cerrado tira InvalidStateError; si nadie lo captura escala a un

    // unhandledrejection y rompe la pantalla. No-op si está cerrado y

    // capturamos cualquier otro fallo (deviceId inválido, permisos).

    if (this.AudioLocalContext.state === "closed") return;

    if (
      "setSinkId" in this.AudioLocalContext &&
      typeof this.AudioLocalContext.setSinkId === "function"
    ) {
      try {
        await this.AudioLocalContext.setSinkId(device.deviceId);
      } catch (error) {
        console.warn("[GlobalAudio] setOutputContext failed:", error);
      }
    }
  }

  async resume() {
    // Con monitor, respeta el interruptor del operador; en el OSR no hay monitor que prender pero
    // la mezcla sí tiene que correr (es lo que se emite).

    if (this.hasLocalMonitor && !isLocalMonitorEnabled()) return;

    if (this.hasLocalMonitor) {
      await this.resumeLocalMonitor();
    }

    if (this.AudioStreamContext.state === "suspended") {
      await this.AudioStreamContext.resume();
    }

    const payloadState = { stream: true, local: this.hasLocalMonitor };

    this.triggerEvent("resume", payloadState);

    this.triggerEvent("state", payloadState);
  }

  private async resumeLocalMonitor() {
    if (this.localGainNode) {
      this.localGainNode.gain.setValueAtTime(
        1,

        this.AudioLocalContext.currentTime,
      );
    }

    if (this.AudioLocalContext.state === "suspended") {
      await this.AudioLocalContext.resume();
    }

    await this.ensureWindowUnmuted();
  }

  async pause() {
    if (this.localGainNode) {
      this.localGainNode.gain.setValueAtTime(
        0,

        this.AudioLocalContext.currentTime,
      );
    }

    const payloadState = { stream: true, local: false };

    this.triggerEvent("pause", payloadState);

    this.triggerEvent("state", payloadState);
  }

  connectVideoAudio(videoElement: HTMLVideoElement, id: string) {
    try {
      if (this.audioSources.has(id)) return;

      const source =
        this.AudioStreamContext.createMediaElementSource(videoElement);

      const gainNode = this.AudioStreamContext.createGain();

      const analyserNode = this.AudioStreamContext.createAnalyser();

      analyserNode.fftSize = 512;

      analyserNode.smoothingTimeConstant = 0.8;

      source.connect(gainNode);

      gainNode.connect(analyserNode);

      analyserNode.connect(this.AudioStreamDestination);

      this.audioSources.set(id, { source, gainNode, analyserNode });
    } catch (error) {
      console.warn("[GlobalAudio] connectVideoAudio error:", error);
    }
  }

  setWindowVolume(deviceId: string, volume: number) {
    const audio = this.audioSources.get(deviceId);

    if (audio) {
      audio.gainNode.gain.setValueAtTime(
        volume,

        this.AudioStreamContext.currentTime,
      );
    }
  }

  setVideoVolume(id: string, volume: number) {
    const audio = this.audioSources.get(id);

    if (audio) {
      audio.gainNode.gain.setValueAtTime(
        volume,

        this.AudioStreamContext.currentTime,
      );
    }
  }

  disconnectVideoAudio(id: string) {
    const audio = this.audioSources.get(id);

    if (audio) {
      audio.source.disconnect();

      audio.gainNode.disconnect();

      audio.analyserNode.disconnect();

      this.audioSources.delete(id);
    }
  }

  /**
   * Connect a guest's WebRTC audio stream into the audio mix.
   * Uses MediaStreamAudioSourceNode since the source is a MediaStream (not a media element).
   */

  connectGuestAudio(id: string, stream: MediaStream) {
    try {
      if (this.audioSources.has(id)) return;

      const audioTracks = stream.getAudioTracks();

      if (audioTracks.length === 0) return;

      const source = this.AudioStreamContext.createMediaStreamSource(stream);

      const gainNode = this.AudioStreamContext.createGain();

      const analyserNode = this.AudioStreamContext.createAnalyser();

      analyserNode.fftSize = 512;

      analyserNode.smoothingTimeConstant = 0.8;

      source.connect(gainNode);

      gainNode.connect(analyserNode);

      analyserNode.connect(this.AudioStreamDestination);

      this.audioSources.set(id, { source, gainNode, analyserNode });
    } catch (error) {
      console.warn("[GlobalAudio] connectGuestAudio error:", error);
    }
  }

  /**
   * Disconnect a guest's audio stream from the audio mix.
   */

  disconnectGuestAudio(id: string) {
    this.disconnectVideoAudio(id);
  }

  /**
   * Set the volume for a guest audio source.
   */

  setGuestVolume(id: string, volume: number) {
    const audio = this.audioSources.get(id);

    if (audio) {
      audio.gainNode.gain.setValueAtTime(
        volume,

        this.AudioStreamContext.currentTime,
      );
    }
  }

  /**
   * Get the analyser node for a guest audio source (for waveform visualization).
   */

  getGuestAnalyser(id: string): AnalyserNode | null {
    return this.getVideoAnalyser(id);
  }

  getVideoAnalyser(id: string): AnalyserNode | null {
    const audio = this.audioSources.get(id);

    return audio?.analyserNode ?? null;
  }

  getConnectedVideoIds(): string[] {
    return Array.from(this.audioSources.keys());
  }

  getVisualizerAnalyser(
    fftSize: number = 512,

    smoothing: number = 0.8,
  ): AnalyserNode {
    if (!this.visualizerAnalyser) {
      this.visualizerAnalyser = this.AudioStreamContext.createAnalyser();

      this.visualizerAnalyser.fftSize = fftSize;

      this.visualizerAnalyser.smoothingTimeConstant = smoothing;

      this.visualizerSource = this.AudioStreamContext.createMediaStreamSource(
        this.AudioStreamDestination.stream,
      );

      this.visualizerSource.connect(this.visualizerAnalyser);
    }

    return this.visualizerAnalyser;
  }

  destroy() {
    if (this.visualizerSource) {
      this.visualizerSource.disconnect();

      this.visualizerSource = null;
    }

    this.visualizerAnalyser = null;

    for (const [id] of this.audioSources) {
      this.disconnectVideoAudio(id);
    }

    this.AudioLocalContext.close();

    this.AudioStreamContext.close();

    if (currentGlobalAudio === this) {
      setCurrentGlobalAudio(null);
    }
  }

  async isLocalDevicePlaying() {
    const gainOn = this.localGainNode
      ? this.localGainNode.gain.value > 0
      : false;

    const ctxRunning = this.AudioLocalContext.state === "running";

    return gainOn && ctxRunning;
  }

  isStreamPlaying() {
    return this.AudioStreamContext.state === "running";
  }

  watch(eventName: string, callback: AudioStreamListener): void {
    if (!this.eventCallbacks.has(eventName)) {
      this.eventCallbacks.set(eventName, []);
    }

    this.eventCallbacks.get(eventName)?.push(callback);
  }

  unwatch(eventName: string, callback: AudioStreamListener): void {
    if (this.eventCallbacks.has(eventName)) {
      const callbacks = this.eventCallbacks.get(eventName)!;

      const index = callbacks.indexOf(callback);

      if (index !== -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  triggerEvent(eventName: string, data?: unknown): void {
    if (this.eventCallbacks.has(eventName)) {
      const callbacks = this.eventCallbacks.get(eventName)!;

      for (const callback of callbacks) {
        callback(data);
      }
    }
  }
}

function isLocalMonitorEnabled(): boolean {
  return localStorage.getItem(LOCAL_MONITOR_KEY) === "true";
}

export default GlobalAudio;
